import React, { useState, useCallback, memo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useDrag } from "react-dnd";
import {
  addFeedToCurrentLayout,
  removeFeedFromLayout,
  addFeedToNewsletter,
} from "actions";
import { TTP_BLOG_URL } from "Config";
import { formatArticle } from "utils";
import _ from "i18n";
import { TEMPLATE_TYPES } from "../../constants";
import { SVG_UP_ICON } from "../../services/svgIcones";
import ToggleButton from "../ui/ToggleButton";
import { SVG_ICONS } from "../../config/Common";
import { toast } from "react-toastify";

const ArticleItem = memo(({ article, isSelected, viewMode, order }) => {
  const [showMore, setShowMore] = useState(false);
  const dispatch = useDispatch();

  // Redux selectors
  const currentLayout = useSelector(
    (state) => state.themes.newsletter.currentLayout,
  );
  const blocks = useSelector((state) => state.themes.newsletter.blocks);
  const base = useSelector((state) => state.themes.newsletter.base);

  // Setup drag using useDrag hook
  const [{ isDragging }, dragRef] = useDrag(
    () => ({
      type: TEMPLATE_TYPES["NEWS_LAYOUT"],
      item: () => formatArticle(article),
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [article],
  );

  const toggleDetails = useCallback(() => {
    setShowMore((prev) => !prev);
  }, []);

  const addArticle = useCallback(() => {
    if (
      base === "TEMPLATE" &&
      (!currentLayout || currentLayout.type !== article.type)
    ) {
      if (!blocks.some(({ feeds, column }) => feeds.length < column)) {
        toast.info(_("All blocks are filled"));
        return;
      }
      dispatch(addFeedToNewsletter({ ...article, type: "NEWS_LAYOUT" }));
      return;
    }
    if (!currentLayout && base !== "TEMPLATE") {
      toast.info(_("Select a layout for your block first"));
      return;
    }

    if (currentLayout.type !== "NEWS_LAYOUT") {
      toast.info(_("Current layout is not the appropriate block type"));
      return;
    }

    dispatch(addFeedToCurrentLayout(article));
  }, [article, base, blocks, currentLayout, dispatch]);

  const removeArticle = useCallback(() => {
    dispatch(removeFeedFromLayout(article.id));
  }, [article.id, dispatch]);

  const renderArticleCover = () => {
    const { cover } = article;
    return (
      <div>
        <div
          className="feed__media"
          style={{ backgroundImage: `url(${cover})` }}
        />
      </div>
    );
  };

  const renderAuthor = () => {
    const { authorName, authorAvatar, publishedAt } = article;
    return (
      <div className="feed_author">
        <div
          className="feed_author-avatar"
          style={{ backgroundImage: `url(${authorAvatar})` }}
        />
        <div className="flex-container flex-dir-column">
          <span className="feed_author-name">{authorName}</span>
          <span className="date">{publishedAt}</span>
        </div>
      </div>
    );
  };

  const {
    id,
    title,
    externalUrl,
    url,
    introduction,
    category,
    color,
    tagsArray,
    language,
  } = article;

  const helpUrl =
    externalUrl && externalUrl.length > 0
      ? externalUrl
      : `${TTP_BLOG_URL + language}/article/${url}/${id}`;

  const listMode = viewMode === "LIST";

  return (
    <div
      ref={dragRef}
      className={`medium-${listMode ? 12 : 6} column`}
      style={{ order, opacity: isDragging ? 0.5 : 1 }}
    >
      <div
        className={`feed ${showMore && "feed-expanded"} ${
          isSelected ? "selected" : ""
        }`}
        style={{ width: "100%" }}
      >
        <img className="feed__expander" src="/img/utils/expander.png" alt="" />
        <div
          className="flex-container flex-dir-column"
          onClick={isSelected ? removeArticle : addArticle}
        >
          <div className="flex-container">
            {renderArticleCover()}
            <div className="feed_content">
              {renderAuthor()}
              <h3
                className="feed_content-title"
                style={
                  !showMore && !listMode
                    ? { overflow: "hidden", height: "2.2rem" }
                    : {}
                }
              >
                <a>{title}</a>
              </h3>
              {listMode && !showMore && (
                <p className="feed_content-summary">
                  {introduction?.slice(0, 210) + "..."}
                </p>
              )}
            </div>
          </div>
          {showMore && (
            <div className="feed-detail">
              <p className="feed_content-summary m-t-s m-b-s">{introduction}</p>
              <div className="feed__tags">
                <span>
                  {tagsArray &&
                    tagsArray.map(({ id, tagName }) => (
                      <a
                        href="#"
                        className={`article_tag-${id}`}
                        key={`tag-${id}-${Math.random()}`}
                      >
                        {tagName}
                      </a>
                    ))}
                </span>
              </div>
              <a href={helpUrl} target="_blank" rel="noopener noreferrer">
                <img
                  src={"/img/icons/" + SVG_ICONS["OPEN_LINK"]}
                  alt="new tab"
                />
              </a>
            </div>
          )}
        </div>
        {category ? (
          <span className="feed__category" style={{ backgroundColor: color }}>
            {category}
          </span>
        ) : null}
        <ToggleButton
          cssClass={"apply-icon"}
          onToggleSelection={isSelected ? removeArticle : addArticle}
          checked={isSelected}
        />
        {isSelected && <div className={"feed-order"}>{order + 1}</div>}
        <div className="read-more" onClick={toggleDetails}>
          {_(showMore ? "close" : "see more")}
          <span className={`${!showMore && "down"}`}>{SVG_UP_ICON}</span>
        </div>
      </div>
    </div>
  );
});

ArticleItem.displayName = "ArticleItem";

export default ArticleItem;
